import {
  MsalGuardConfiguration,
  MsalInterceptorConfiguration,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { environment } from '../environments/environment';

export function MsalInstanceFactory(): IPublicClientApplication {
  const msalConfig = environment.msalConfig;
  const redirectUri = environment.redirectUri;
  const postLogoutRedirectUri = environment.redirectUri;

  return new PublicClientApplication({
    auth: {
      clientId: msalConfig.auth.clientId,
      authority: msalConfig.auth.authority,
      redirectUri,
      postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage,
    },
    system: {
      loggerOptions: {
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MsalGuardFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment['apiConfig'].scopes],
    },
    loginFailedRoute: '/login-failed',
  };
}

export function MsalInterceptorFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  const scopes = environment['apiConfig'].scopes;

  const normalizeProtectedResourceUrl = (
    resourceUrl: string | undefined,
  ): string | null => {
    if (!resourceUrl) {
      return null;
    }

    const trimmed = resourceUrl.trim();

    if (!trimmed) {
      return null;
    }

    const sanitize = (url: string): string =>
      url.endsWith('/') ? url.slice(0, -1) : url;

    if (/^https?:\/\//i.test(trimmed)) {
      return sanitize(trimmed);
    }

    const origin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : null;

    if (!origin) {
      return null;
    }

    try {
      return sanitize(new URL(trimmed, origin).href);
    } catch {
      return null;
    }
  };

  const addProtectedResource = (
    resourceUrl: string | undefined,
    defaultScope?: boolean,
  ): void => {
    const normalized = normalizeProtectedResourceUrl(resourceUrl);

    if (!normalized || protectedResourceMap.has(normalized)) {
      return;
    }

    protectedResourceMap.set(
      normalized,
      defaultScope
        ? scopes
        : [environment.msalConfig.auth.clientId + '/.default'],
    );
  };

  addProtectedResource(environment['apiConfig'].uri, true);
  addProtectedResource(environment['apiUrl']);
  addProtectedResource(environment['mwApiUrl']);
  addProtectedResource(environment['baseUrl']);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
    strictMatching: false,
  };
}
